import { Tooltip } from 'antd';
import { useMemo } from 'react';
import styled from 'styled-components';

import EnterprisePlanIcon from '../../assets/icons/EnterprisePlanIcon';
import theme from '../../assets/theme';
import { LicenseCoarseLevels, UserAccessBadge } from '../../hooks/useAccessBadge';
import { useMetrics } from '../../hooks/useMetrics';
import { GeneratorCoarseLicenseLevel } from '../../services/TemplateService/Template.type';
import { IconWrapper } from './IconWrapper';

const PlanTypeLabel = ({
  userAccessBadge,
  onlyIcon,
  isGeneratorCard,
  generatorPlan,
  isPremiumGenerator,
}: {
  userAccessBadge?: UserAccessBadge;
  onlyIcon?: boolean;
  isGeneratorCard?: boolean;
  generatorPlan?: GeneratorCoarseLicenseLevel | null;
  isPremiumGenerator: boolean | null | undefined;
}) => {
  const metrics = useMetrics();
  const isPremiumPlanGenerator =
    generatorPlan === GeneratorCoarseLicenseLevel.premium ||
    (generatorPlan === undefined && isPremiumGenerator);
  const isEnterprisePlanGenerator =
    generatorPlan === GeneratorCoarseLicenseLevel.enterprise;

  const hasAccessToGenerator =
    (isPremiumPlanGenerator &&
      (userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.premium ||
        userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise)) ||
    (isEnterprisePlanGenerator &&
      userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise);

  const getAccessStatus = (
    plan: GeneratorCoarseLicenseLevel,
    hasAccess: boolean | null | undefined,
  ) => {
    const statuses = {
      [GeneratorCoarseLicenseLevel.premium]: {
        withAccess: {
          tooltip:
            'As a Curipod premium subscriber, you get this premium customizable lesson for free',
          label: 'Premium',
        },
        withoutAccess: {
          tooltip: 'Curipod premium subscribers get this customizable lesson for free',
          label: 'Premium',
        },
      },
      [GeneratorCoarseLicenseLevel.enterprise]: {
        withAccess: {
          tooltip:
            'As a Curipod school plan subscriber, you get this customizable lesson for free',
          label: 'School Plan',
        },
        withoutAccess: {
          tooltip:
            'Curipod school plan subscribers get this customizable lesson for free',
          label: 'Request school access to unlock',
        },
      },
      [GeneratorCoarseLicenseLevel.freemium]: {
        tooltip: 'Free customizable lesson',
        label: '',
      },
    };

    if (
      plan === GeneratorCoarseLicenseLevel.premium ||
      plan === GeneratorCoarseLicenseLevel.enterprise
    ) {
      return hasAccess ? statuses[plan].withAccess : statuses[plan].withoutAccess;
    }
    return statuses[GeneratorCoarseLicenseLevel.freemium];
  };

  const planType =
    generatorPlan ||
    (isPremiumGenerator
      ? GeneratorCoarseLicenseLevel.premium
      : GeneratorCoarseLicenseLevel.freemium);
  const hasAccess =
    (planType === GeneratorCoarseLicenseLevel.premium &&
      (userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.premium ||
        userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise)) ||
    (planType === GeneratorCoarseLicenseLevel.enterprise &&
      userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise);

  const accessStatus = useMemo(() => {
    return getAccessStatus(planType, hasAccess);
  }, [planType, hasAccess]);

  return (
    <Tooltip title={accessStatus.tooltip}>
      <Label
        $align="right"
        $isGeneratorCard={isGeneratorCard}
        $hasClick={isEnterprisePlanGenerator && !hasAccessToGenerator && !onlyIcon}
        onClick={() => {
          if (isEnterprisePlanGenerator && !hasAccessToGenerator && !onlyIcon) {
            metrics.logEvent('Enterprise.Label.Upgrade.Clicked', {
              location: 'enterpriseLabel',
            });
            window.open(`/quotes`, '__blank');
          }
        }}
      >
        <PlanIcon
          isPremiumPlanGenerator={isPremiumPlanGenerator}
          isEnterprisePlanGenerator={isEnterprisePlanGenerator}
          onlyIcon={onlyIcon}
          hasAccessToGenerator={hasAccessToGenerator}
        />
        {!onlyIcon && <>{accessStatus.label}</>}
      </Label>
    </Tooltip>
  );
};

export const PlanIcon = ({
  isPremiumPlanGenerator,
  isEnterprisePlanGenerator,
  onlyIcon,
  hasAccessToGenerator,
}: {
  isPremiumPlanGenerator: boolean | null | undefined;
  isEnterprisePlanGenerator: boolean;
  onlyIcon?: boolean;
  hasAccessToGenerator: boolean | null | undefined;
}) => {
  return (
    <IconWrapper
      width="12px"
      height="12px"
      style={{
        color: hasAccessToGenerator ? theme.colors.white : theme.colors.yellow,
        marginRight: onlyIcon ? 0 : 5,
      }}
    >
      {isEnterprisePlanGenerator && <EnterprisePlanIcon />}
      {isPremiumPlanGenerator && <EnterprisePlanIcon />}
    </IconWrapper>
  );
};

const Label = styled.div<{
  $align: 'right' | 'left';
  $isGeneratorCard?: boolean;
  $hasClick?: boolean;
}>`
  position: ${({ $isGeneratorCard }) => ($isGeneratorCard ? 'absolute' : 'unset')};
  bottom: 5px;
  right: ${({ $align }) => ($align === 'right' ? '5px' : 'unset')};
  left: ${({ $align }) => ($align === 'left' ? '5px' : 'unset')};
  background: #00000099;
  border-radius: 10px;
  color: ${theme.colors.white};
  font-weight: 600;
  font-size: 12px;
  padding: 2px 10px;
  height: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0px 4px 4px #00000025;
  width: fit-content;
  cursor: ${({ $hasClick }) => ($hasClick ? 'pointer' : 'unset')};
`;

export default PlanTypeLabel;
